import { Button, Icon, makeStyles, Menu, TextField, Theme } from '@material-ui/core';
import { BaseEntityField, isInputFieldHidden, useDropdown } from '@moderepo/mode-app-components';
import { Entity } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import React, { useState, useCallback } from 'react';


import { EntitiesListingContainer, EntitiesListingContainerProps } from '../../containers';


const useStyle = makeStyles((theme: Theme) => {
    return {

        menuTrigger: {
            '&.editable': {
                cursor   : 'pointer',
                '& input': {
                    cursor: 'pointer',
                },
            },

            '& .icon': {
                color: theme.palette.action.disabled,
            },
        },

        menu: {
            width                       : '100%',
            minHeight                   : 300,
            height                      : 500,
            maxHeight                   : '100%',
            [theme.breakpoints.up('md')]: {
                width: 600,
            },
            padding: 0,
            '& ul' : {
                width  : '100%',
                height : '100%',
                padding: 0,
            },
        },

        entitiesListWrapper: {
            width        : '100%',
            height       : '100%',
            padding      : 0,
            display      : 'flex',
            flexDirection: 'column',

            '& .actions-bar': {
                padding    : theme.spacing(1),
                textAlign  : 'right',
                '& .action': {
                    marginLeft: theme.spacing(1),
                },
            },
        },

        entitiesListContainer: {
            height  : 0,
            flex    : 1,
            width   : '100%',
            overflow: 'auto',
        },
    };
}, {
    name: 'EntityInfoComp', index: 1,
});



/**
 * When setting up 'EntityInputField', we need the container to provide the props for the entities listing container e.g. projectId, homeId,
 * etc... However, we don't want the container to be able to configure everything. There are a few props that need to be controlled by this
 * component therefore we will create an interface that extends EntitiesListingContainerProps and remove some of the props that the container
 * should not provide.
 *
 * NOTE: Typescript Omit can only Omit 1 prop at a time therefore we need to chain them up to omit multiple props.
 */
interface SubsetEntitiesListingContProps extends
    Omit<Omit<Omit<EntitiesListingContainerProps, 'canCreateEntities'>, 'showPreviewOption'>, 'onEntitySelected'>
{}


/**
 * Entity select dropdown requires additional props for entities listing container so we need to extends BaseEntityField
 * and add 'entitiesListingContainerProps' which is a subset of EntitiesListingContainerProps
 */
export interface EntityInputField extends BaseEntityField<Entity> {
    readonly entitiesListingContainerProps: SubsetEntitiesListingContProps,
    readonly texts: {
        readonly clearButtonLabel: string;
        readonly cancelButtonLabel: string;
        readonly selectButtonLabel: string;
        readonly emptyValueDisplay?: string;           // OPTIONAL value to display if an entity is not selected. Default will be empty string.
    }
}


export interface EntityInputCompProps extends EntityInputField {
    readonly className?: string | undefined;
    readonly isEditing?: boolean | undefined;
    readonly fieldName: string;
    readonly onFieldClicked?: (fieldName: string)=> void;
    readonly onFormInputChange?: (fieldName: string, value: any)=> void;
}


/**
 * This is an Input component for selecting an Entity. This component will use a TextField to display the selected entity's
 * ID and name and use the EntitiesListingContainer to show the list of entities the user can select from.
 */
export const EntityInputComp: React.FC<EntityInputCompProps> = (props: EntityInputCompProps) => {

    const [entitiesMenuAnchorEl, isEntitiesMenuOpened, onEntitiesMenuAnchorElClicked, onEntitiesMenuClosed] = useDropdown();
    const [selectedEntity, setSelectedEntity] = useState<Entity | undefined>(props.value);
    const classes = useStyle();

    const onClearInput = useCallback(() => {
        if (props.onFormInputChange) {
            props.onFormInputChange(props.fieldName, undefined);
        }
        onEntitiesMenuClosed();
    }, [onEntitiesMenuClosed, props]);


    const onCancel = useCallback(() => {
        onEntitiesMenuClosed();
    }, [onEntitiesMenuClosed]);


    const onSelectInput = useCallback(() => {
        if (props.onFormInputChange) {
            props.onFormInputChange(props.fieldName, selectedEntity);
        }
        onEntitiesMenuClosed();
        setSelectedEntity(undefined);
    }, [onEntitiesMenuClosed, props, selectedEntity]);


    if (isInputFieldHidden(props.hidden, props.hideOnEdit, props.editable, props.editingModeOnly, props.isEditing)) {
        return null;
    }

    return (
        <>
            <TextField
                className={clsx(props.className,
                    !props.editable ? 'readonly' : '',
                    !props.isEditing && props.clickable && 'clickable',
                    props.isEditing && !props.editable ? 'fade' : '')}

                required={props.isEditing && props.required}
                variant="standard"
                type="text"
                fullWidth
                label={props.label}
                InputLabelProps={{
                    shrink   : true,
                    className: props.editable ? 'editable-label-icon' : '',
                }}
                InputProps={{
                    className   : clsx('input-wrapper', classes.menuTrigger),
                    readOnly    : true,
                    endAdornment: (
                        <Icon className="icon">arrow_drop_down</Icon>
                    ),
                }}
                error={Boolean(props.error)}
                helperText={props.error ? props.error : ''}
                value={props.value ? props.value.displaySettings.name : props.texts.emptyValueDisplay ?? ''}
                onClick={props.editable ? (event) => {
                    onEntitiesMenuAnchorElClicked(event);
                    if (props.onFieldClicked) {
                        props.onFieldClicked(props.fieldName);
                    }
                } : undefined}
            />

            <Menu
                getContentAnchorEl={null}
                anchorEl={entitiesMenuAnchorEl}
                anchorOrigin={{
                    vertical  : 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical  : 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    className: classes.menu,
                }}
                keepMounted
                open={isEntitiesMenuOpened}
                onClose={onEntitiesMenuClosed}
            >
                <span className={classes.entitiesListWrapper}>
                    <EntitiesListingContainer
                        {...props.entitiesListingContainerProps}
                        className={clsx(classes.entitiesListContainer)}
                        canCreateEntities={false}
                        disablePreviewOption
                        minimalUI
                        onEntitySelected={(entity: Entity) => {
                            setSelectedEntity(entity);
                        }}
                    />
                    <div className="actions-bar">
                        {/* If this input is not required, show 'Clear' button to set the value to 'undefined' */}
                        {!props.required && (
                            <Button
                                className="action"
                                color="primary"
                                size="small"
                                disabled={!props.value}
                                onClick={onClearInput}
                            >
                                {props.texts.clearButtonLabel}
                            </Button>
                        )}
                        <Button
                            className="action"
                            size="small"
                            onClick={onCancel}
                        >
                            {props.texts.cancelButtonLabel}
                        </Button>
                        <Button
                            className="action"
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={!selectedEntity}
                            onClick={onSelectInput}
                        >
                            {props.texts.selectButtonLabel}
                        </Button>
                    </div>
                </span>
            </Menu>
        </>
    );
};
